import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { Block as SingleBlock } from '../TrajectoryDetail';
import {
  resetPresentationMode,
  setAnimation,
  setHandleDownArrow,
  setHandleUpArrow,
} from 'src/modules/shared/slices/presentationMode/presentationModeSlice';
import useKeyBindings from 'src/modules/shared/hooks/useKeyBindings';
import './styles.css';
import { BlockProps } from '../TrajectoryDetail/Block';
import { scrollToViewById } from 'src/modules/shared/helpers/scroll';
import {
  resetFullscreenMode,
  setFullscreenScaleFactor,
} from 'src/modules/shared/slices/screenMode/entity/fullScreenModeSlice';
import { useFullScreen } from 'src/modules/shared/hooks/useFullScreen';
import { setBlockToScroll } from 'src/modules/shared/slices/blocks/entity/BlocksSlice';
import theme from 'src/config/theme';
import { PresentationModeNavBar } from './components/PresentationModeNavBar';

export const PresentationMode: FC = () => {
  const dispatch = useAppDispatch();
  const presentingIndex = useAppSelector((state) => state.presentationMode.presentingIndex);
  const animation = useAppSelector((state) => state.presentationMode.animation);
  const blocksToPresent = useAppSelector((state) => state.presentationMode.blocksToPresent);
  const isNavigationBarOpen = useAppSelector((state) => state.navigationBar.isNavigationBarOpen);
  const isOpen = useAppSelector((state) => state.sidepanel.isOpen);
  const width = useAppSelector((state) => state.sidepanel.sidepanel?.width);
  const pageWidth = useAppSelector((state) => state.privatePageSettings.pageWidth);
  const pageId = useAppSelector((state) => state.trajectories.currentPageId);

  const [elementWidth, setElementWidth] = useState(
    document.getElementById('private_page_scroll_container_unused')?.clientWidth
  );

  useEffect(() => {
    const handleResize = () =>
      setElementWidth(document.getElementById('private_page_scroll_container_unused')?.clientWidth);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let newElement =
      (elementWidth! -
        (width! as number) -
        (isNavigationBarOpen
          ? theme.config.openNavigationBarWidth
          : theme.config.closedNavigationBarWidth)) /
      (pageWidth * 10);
    const newFactor = newElement < 1.15 ? 1.15 : newElement > 1.35 ? 1.35 : newElement;

    dispatch(setFullscreenScaleFactor(newFactor));
  }, [dispatch, elementWidth, width, isOpen, isNavigationBarOpen, pageWidth]);

  useEffect(() => {
    if (presentingIndex || presentingIndex === 0) {
      scrollToViewById('private_page_wrapper');
    }
  }, [dispatch, presentingIndex]);

  const { exitFullScreen } = useFullScreen();
  const handleClosePresentationMode = useCallback(() => {
    exitFullScreen();
    dispatch(resetFullscreenMode());
    dispatch(resetPresentationMode());
  }, [dispatch, exitFullScreen]);

  const { requestFullScreen } = useFullScreen(handleClosePresentationMode);

  const handleFullScreen = useCallback(() => {
    requestFullScreen('body');
  }, [requestFullScreen]);

  useEffect(() => {
    handleFullScreen();
  }, [handleFullScreen]);

  useEffect(() => {
    if (animation === 'bounce') {
      setTimeout(() => {
        dispatch(setAnimation(null));
      }, 500);
    }
    if (animation === 'appear-from-bottom' || animation === 'appear-from-top') {
      setTimeout(() => {
        dispatch(setAnimation(null));
      }, 500);
    }
  }, [animation, dispatch]);

  useKeyBindings({
    ArrowUp: () => dispatch(setHandleUpArrow()),
    ArrowLeft: () => dispatch(setHandleUpArrow()),
    PageUp: () => dispatch(setHandleUpArrow()),
    ArrowRight: () => dispatch(setHandleDownArrow()),
    PageDown: () => dispatch(setHandleDownArrow()),
    ArrowDown: () => dispatch(setHandleDownArrow()),
    Escape: () => {
      handleClosePresentationMode();
    },
  });

  useEffect(
    () => () => {
      handleClosePresentationMode();
    },
    [handleClosePresentationMode]
  );

  useEffect(() => {
    if (!blocksToPresent || !presentingIndex) return;
    if (blocksToPresent) {
      let newId = null;
      if (
        blocksToPresent[presentingIndex] &&
        blocksToPresent[presentingIndex].sections &&
        blocksToPresent[presentingIndex].sections[0]
      ) {
        newId = `block_wrapper-${blocksToPresent[presentingIndex].sections[0].id}`;
      }
      dispatch(setBlockToScroll(newId));
    }
  }, [blocksToPresent, dispatch, presentingIndex]);

  if (!blocksToPresent) return null;

  return (
    <div className="presentation-container" style={{ position: 'relative', minHeight: '0%' }}>
      <PresentationModeNavBar />
      {blocksToPresent.map((block, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            display: index === presentingIndex ? 'block' : 'none',
            animationName: index === presentingIndex ? animation : 'none',
            animationDuration: index === presentingIndex ? '0.5s' : '0s',
          }}
        >
          <SingleBlock
            pageId={pageId ?? ''}
            pageType="opbouw"
            readOnly
            block={block as BlockProps}
          />
        </div>
      ))}
    </div>
  );
};
